// QuickAddTask — Theme K6 / Story Q5.
//
// Single-line natural-language task input for quick task creation within a
// phase. Uses parseQuickAdd() to extract priority, due date, and @assignee
// from the typed string and shows live preview chips while typing.
//
// Design decisions:
// - `end_date` is the due-date field used by createTask (maps dueDate ISO).
// - Priority maps directly: the parser returns "low"|"medium"|"high"|"urgent"
//   which match TaskPriority exactly (TASK_PRIORITIES from @juuplan/shared).
// - Assignee resolution is advisory: if exactly one user's name (case-insensitive
//   prefix match) matches assigneeName, that user's id is sent as assignee_ids[0].
//   Multiple or zero matches → assignee_ids omitted.
// - ToastError is surfaced via the shared toast signal (same as rest of app).
// - Input is disabled while the POST is in flight; re-enables on success/error.

import { useState, useRef } from "preact/hooks";
import type { TaskCreate, TaskPriority } from "@juuplan/shared";
import { parseQuickAdd } from "../lib/quickAdd";
import type { ParsedQuickAdd } from "../lib/quickAdd";
import { createTask } from "../store/project";
import { useUsers } from "../store/users";
import { toastError } from "../lib/toast";
import { aiParseTask, ApiError } from "../lib/api";
import { useMe } from "../lib/auth";
import { applyAiDraft, buildQuickAddString } from "../lib/aiDraft";
import { TASK_PRIORITY_VAR, softPillStyle } from "../lib/format";

// Priority label map for the preview chip
const PRIORITY_LABEL: Record<string, string> = {
  urgent: "!dringend",
  high: "!hoch",
  medium: "!mittel",
  low: "!niedrig",
};

export interface QuickAddTaskProps {
  /** Phase id to create the task in. */
  phaseId: number;
  /** Called after a task is successfully created (e.g. to trigger re-fetch). */
  onCreated?: () => void;
  /** Project id — required to show the KI-parse affordance. */
  projectId?: number;
}

/**
 * QuickAddTask — natural-language task creation input with live chip preview.
 *
 * Renders a text input; as the user types, parseQuickAdd extracts tokens and
 * shows small chips for priority, due date, and @assignee. On Enter the task
 * is POSTed to `POST /api/phases/:phaseId/tasks` (via createTask store helper)
 * and the input is cleared.
 */
export function QuickAddTask(props: QuickAddTaskProps): preact.JSX.Element {
  const { phaseId, onCreated, projectId } = props;
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const users = useUsers();
  const me = useMe();
  const aiEnabled = me.user?.ai_enabled === true && projectId !== undefined;

  // KI-parse affordance state.
  const [parseOpen, setParseOpen] = useState(false);
  const [parseText, setParseText] = useState("");
  const [parseBusy, setParseBusy] = useState(false);
  const [parse503, setParse503] = useState(false);

  const parsed: ParsedQuickAdd = parseQuickAdd(value);

  /**
   * Resolve an @assignee name to a user id.
   * Returns the user id only when exactly one user's name matches
   * (case-insensitive substring match on the full name). Ambiguous or
   * unmatched → undefined.
   */
  const resolveAssignee = (name: string): number | undefined => {
    if (!name || users.items.length === 0) return undefined;
    const lower = name.toLowerCase();
    const matches = users.items.filter((u) =>
      u.name.toLowerCase().includes(lower)
    );
    return matches.length === 1 ? matches[0]!.id : undefined;
  };

  const handleKeyDown = async (e: KeyboardEvent): Promise<void> => {
    if (e.key !== "Enter") return;
    e.preventDefault();

    const title = parsed.title.trim();
    if (!title || busy) return;

    setBusy(true);
    try {
      const body: TaskCreate = { title };

      if (parsed.priority) {
        body.priority = parsed.priority;
      }

      if (parsed.dueDate) {
        body.end_date = parsed.dueDate;
      }

      if (parsed.assigneeName) {
        const userId = resolveAssignee(parsed.assigneeName);
        if (userId !== undefined) {
          body.assignee_ids = [userId];
        }
        // If unresolved or ambiguous: silently omit — advisory only.
      }

      await createTask(phaseId, body);
      setValue("");
      onCreated?.();
    } catch {
      // createTask surfaces errors via toastError internally; we add a fallback
      // toast only if somehow the error slipped through silently.
      toastError("Aufgabe konnte nicht erstellt werden.");
    } finally {
      setBusy(false);
      // Return focus to the input so the user can immediately type the next task.
      inputRef.current?.focus();
    }
  };

  /**
   * Call the KI parse-task endpoint and prefill the quick-add input.
   * Never auto-creates; the user must still press Enter.
   */
  const handleParse = async (): Promise<void> => {
    const text = parseText.trim();
    if (!text || parseBusy || projectId === undefined) return;
    setParseBusy(true);
    setParse503(false);
    try {
      const draft = await aiParseTask(text, projectId);
      const fields = applyAiDraft(draft);
      setValue(buildQuickAddString(fields));
      // Close the parse panel so the user can edit/submit the prefilled input.
      setParseOpen(false);
      setParseText("");
      inputRef.current?.focus();
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setParse503(true);
      }
      // Other errors fall through to auto-toast (api() default behaviour).
    } finally {
      setParseBusy(false);
    }
  };

  const hasChips =
    parsed.priority !== undefined ||
    parsed.dueDate !== undefined ||
    parsed.assigneeName !== undefined;

  return (
    <div class="flex flex-col gap-1 px-3 py-2 border-t border-[color:var(--color-outline-variant)]/60 bg-[color:var(--color-surface-container-low)]/20">
      <div class="flex items-center gap-2">
        {/* Muted "+" indicator */}
        <span
          class="text-[color:var(--color-on-surface-variant)] text-sm select-none"
          aria-hidden="true"
        >
          +
        </span>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onInput={(e) => setValue((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => void handleKeyDown(e)}
          disabled={busy}
          placeholder="Aufgabe schnell hinzufügen … z. B. 'Bug fixen morgen !hoch'"
          aria-label="Aufgabe schnell hinzufügen"
          maxLength={200}
          class="flex-1 min-w-0 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 transition-colors disabled:opacity-60"
        />
      </div>

      {/* Live preview chips — only shown when at least one token is detected */}
      {hasChips && (
        <div
          class="flex flex-wrap items-center gap-1 pl-5"
          aria-label="Erkannte Felder"
          data-testid="quick-add-chips"
        >
          {parsed.priority && (
            <span
              class="inline-flex items-center rounded-full px-2 py-0.5 text-label font-medium"
              style={softPillStyle(TASK_PRIORITY_VAR[parsed.priority as TaskPriority])}
              data-testid={`chip-priority-${parsed.priority}`}
            >
              {PRIORITY_LABEL[parsed.priority] ?? parsed.priority}
            </span>
          )}
          {parsed.dueDate && (
            <span
              class="inline-flex items-center rounded-full px-2 py-0.5 text-label font-medium bg-[color:var(--color-surface-container)] text-[color:var(--color-on-surface-variant)]"
              data-testid="chip-date"
            >
              {parsed.dueDate}
            </span>
          )}
          {parsed.assigneeName && (
            <span
              class="inline-flex items-center rounded-full px-2 py-0.5 text-label font-medium bg-[color:var(--color-surface-container)] text-[color:var(--color-on-surface-variant)]"
              data-testid="chip-assignee"
            >
              @{parsed.assigneeName}
            </span>
          )}
          {/* Title preview — dimmed */}
          {parsed.title && parsed.title !== value.trim() && (
            <span
              class="text-label text-[color:var(--color-on-surface-variant)]/60 ml-1 truncate max-w-[200px]"
              title={parsed.title}
              data-testid="chip-title-preview"
            >
              Titel: „{parsed.title}"
            </span>
          )}
        </div>
      )}

      {/* KI-parse affordance — only when ai_enabled and projectId is set */}
      {aiEnabled && (
        <div class="pl-5">
          {!parseOpen ? (
            <button
              type="button"
              onClick={() => setParseOpen(true)}
              class="text-label text-[color:var(--color-primary)] hover:underline focus:outline-none"
              data-testid="ki-parse-toggle"
            >
              Mit KI parsen
            </button>
          ) : (
            <div class="flex flex-col gap-1 mt-1" data-testid="ki-parse-panel">
              <textarea
                value={parseText}
                onInput={(e) => setParseText((e.target as HTMLTextAreaElement).value)}
                placeholder="Beschreibe die Aufgabe frei …"
                rows={2}
                disabled={parseBusy}
                aria-label="Aufgabe frei beschreiben"
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] resize-none disabled:opacity-60"
                data-testid="ki-parse-textarea"
              />
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void handleParse()}
                  disabled={parseBusy || !parseText.trim()}
                  class="rounded bg-[color:var(--color-primary)] px-2 py-0.5 text-label font-medium text-[color:var(--color-on-primary)] hover:opacity-90 disabled:opacity-50 focus:outline-none"
                  data-testid="ki-parse-submit"
                >
                  {parseBusy ? "Parsen …" : "Parsen"}
                </button>
                <button
                  type="button"
                  onClick={() => { setParseOpen(false); setParseText(""); setParse503(false); }}
                  class="text-label text-[color:var(--color-on-surface-variant)] hover:underline focus:outline-none"
                  data-testid="ki-parse-cancel"
                >
                  Abbrechen
                </button>
              </div>
              {parse503 && (
                <p
                  class="text-label text-[color:var(--color-error)]"
                  data-testid="ki-parse-503"
                  aria-live="polite"
                >
                  KI ist derzeit deaktiviert.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
